import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * "Inspire me" popover — self-contained: owns its open state, outside-click
 * handler, and deal fetch. Parent just provides the current origin + a picker
 * callback invoked when a deal is chosen.
 */
export default function InspirePopover({ origin, onPickDeal }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openPopover = async () => {
    setOpen(true);
    if (deals.length > 0) return;
    setLoading(true);
    try {
      const query = origin ? `/deals?status=published&limit=3&origin_iata=${origin}` : `/deals?status=published&limit=3`;
      let { data } = await api.get(query);
      if (!data.items?.length && origin) {
        const fallback = await api.get(`/deals?status=published&limit=3`);
        data = fallback.data;
      }
      setDeals(data.items || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Inspire deals load failed:", err?.message || err);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={openPopover}
        className="flex items-center gap-1.5 border border-[#1B3B36] text-[#1B3B36] px-3 py-1.5 text-[11px] tracking-wider uppercase font-mono hover:bg-[#1B3B36] hover:text-[#F9F9F6] transition-colors"
        data-testid="sticky-inspire-btn"
      >
        <Sparkle size={12} weight="regular" />
        Inspire me
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[min(92vw,480px)] bg-white border border-[#D6CFC4] shadow-[0_12px_32px_rgba(28,26,23,0.15)] z-50"
          data-testid="inspire-popover"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#D6CFC4] bg-[#EFECE5]">
            <div>
              <div className="overline text-[#B4533C]">Inspire me</div>
              <div className="font-serif text-lg leading-tight text-[#1C1A17] mt-0.5">
                {origin ? `Deals from ${origin}` : "Editor-picked deals"}
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-[#4A4742] hover:text-[#1C1A17]" aria-label="Close" data-testid="inspire-close">
              <X size={16} />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center font-mono text-xs text-[#4A4742]">Looking for deals…</div>
            ) : deals.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#4A4742]">No live deals right now — try a manual search.</div>
            ) : (
              deals.map((deal) => (
                <InspireDealRow
                  key={deal.id}
                  deal={deal}
                  onPick={() => {
                    const iata = deal.destination?.match(/\(([A-Z]{3})\)/)?.[1];
                    if (iata) onPickDeal(iata);
                    setOpen(false);
                  }}
                />
              ))
            )}
          </div>
          <div className="border-t border-[#D6CFC4] px-5 py-3 bg-[#F9F9F6]">
            <Link to="/deals" className="overline text-[#1B3B36] hover:text-[#B4533C]" data-testid="inspire-all-deals">All deals →</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function InspireDealRow({ deal, onPick }) {
  const destIata = deal.destination?.match(/\(([A-Z]{3})\)/)?.[1];
  return (
    <Link
      to={`/deal/${deal.slug}`}
      onClick={onPick}
      className="flex gap-4 p-4 border-b border-[#EFECE5] last:border-b-0 hover:bg-[#F9F9F6] group"
      data-testid={`inspire-deal-${deal.slug}`}
    >
      <div className="w-24 aspect-[4/3] bg-[#EFECE5] overflow-hidden shrink-0">
        {deal.featured_image && <img src={deal.featured_image} alt={deal.title} className="w-full h-full object-cover" loading="lazy" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="overline text-[#B4533C] text-[10px]">{deal.airline || "Deal alert"}{destIata ? ` · ${destIata}` : ""}</div>
        <div className="font-serif text-base leading-tight mt-1 group-hover:text-[#B4533C] text-[#1C1A17]">{deal.title}</div>
        <div className="mt-2 flex items-center gap-3 text-xs font-mono text-[#4A4742]">
          {deal.cash_price && <span className="text-[#1B3B36]">${deal.cash_price}</span>}
          {deal.points_price && <span>{deal.points_price.toLocaleString()} pts</span>}
          <ArrowRight size={12} className="ml-auto text-[#B4533C] opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </Link>
  );
}
